import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { CapService } from '../api';

interface IUrlParams {
  merchantId: string | null;
  amount: string | null;
  cryptoCurrency: string | null;
  currency: string | null;
  email: string | null;
  env: string | null;
}

interface IUseCapSessionResult {
  sessionId: string | null;
  checkoutId: string | null;
  urlParams: IUrlParams;
  isLoading: boolean;
  isCreatingSession: boolean;
  error: string | null;
  env: string;
  updateSessionAndCheckout: (data: {
    amount: number;
    currency: string;
    customer: {
      email?: string;
      firstName?: string;
      lastName?: string;
      country?: string;
      allowMerchantPayment?: boolean;
    };
  }) => Promise<boolean>;
  getWidgetUrl: () => string | null;
}

/**
 * Custom hook for CAP payment session
 * Handles session creation from URL params and customer data updates
 */
export const useCapSession = (): IUseCapSessionResult => {
  const location = useLocation();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [checkoutId, setCheckoutId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingSession, setIsCreatingSession] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const sessionCreationAttemptedRef = useRef(false);

  // Get URL params (support multiple naming conventions)
  const urlParams = useMemo((): IUrlParams => {
    const params = new URLSearchParams(location.search);
    
    return {
      merchantId: params.get('merchantId') || params.get('merchant_id'),
      amount: params.get('amount'),
      // Support multiple naming: cryptoCurrency, crypto_currency, cryptocurrency
      cryptoCurrency: params.get('cryptoCurrency') || params.get('crypto_currency') || params.get('cryptocurrency'),
      currency: params.get('currency'),
      email: params.get('email'),
      env: params.get('env'),
    };
  }, [location.search]);

  const capService = useMemo(() => {
    // If no env, CapService will throw error when used
    return urlParams.env ? new CapService(urlParams.env) : null;
  }, [urlParams.env]);

  /**
   * Create initial session with merchantId and optionally cryptoCurrency
   */
  const createInitialSession = useCallback(async () => {
    if (!urlParams.merchantId || sessionCreationAttemptedRef.current) {
      setIsCreatingSession(false);
      return;
    }

    if (!urlParams.env) {
      setError('env parameter is required in URL');
      setIsCreatingSession(false);
      sessionCreationAttemptedRef.current = true;
      return;
    }

    if (!capService) {
      setError('Failed to initialize CAP service');
      setIsCreatingSession(false);
      sessionCreationAttemptedRef.current = true;
      return;
    }

    sessionCreationAttemptedRef.current = true;
    setIsCreatingSession(true);
    setError(null);

    try {
      // Save env to localStorage for status page redirect
      localStorage.setItem('cap_env', urlParams.env);
      console.log('Saved env to localStorage:', urlParams.env);

      const createData = {
        integer_tracking_id: urlParams.merchantId,
        crypto_currency: urlParams.cryptoCurrency || undefined,
      };
      
      console.log('Create session data:', createData);
      
      const response = await capService.createSession(createData);

      console.log('Created session ID:', response.session_id);
      setSessionId(response.session_id);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create session';
      setError(errorMessage);
      console.error('Failed to create session:', err);
    } finally {
      setIsCreatingSession(false);
    }
  }, [urlParams.merchantId, urlParams.cryptoCurrency, urlParams.env, capService]);

  /**
   * Update session with payment data and customer info, then get checkout_id
   */
  const updateSessionAndCheckout = useCallback(
    async (data: {
      amount: number;
      currency: string;
      customer: {
        email?: string;
        firstName?: string;
        lastName?: string;
        country?: string;
        allowMerchantPayment?: boolean;
      };
    }) => {
      if (!sessionId) {
        setError('Session ID is required');
        return false;
      }

      if (!capService) {
        setError('CAP service is not initialized');
        return false;
      }

      setIsLoading(true);
      setError(null);

      try {
        const updateData = {
          checkout_data: {
            amount: data.amount,
            currency: data.currency,
          },
          customer: {
            email: data.customer.email,
            firstName: data.customer.firstName,
            lastName: data.customer.lastName,
            country: data.customer.country,
            allowMerchantPayment: data.customer.allowMerchantPayment,
          },
        };
        
        console.log('Session ID:', sessionId);
        console.log('Update session data:', updateData);
        
        const response = await capService.updateSession(sessionId, updateData);
        
        console.log('Update session response:', response);

        // Store checkout_id from response
        if (response.checkout_id) {
          console.log('Got checkout_id:', response.checkout_id);
          setCheckoutId(response.checkout_id);
        }

        return true;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update session';
        setError(errorMessage);
        console.error('Failed to update session:', err);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [sessionId, capService]
  );

  /**
   * Get payment widget URL
   */
  const getWidgetUrl = useCallback(() => {
    if (!sessionId || !checkoutId || !capService) {
      return null;
    }
    
    // Always redirect to status page
    const redirectUrl = `${window.location.origin}/cap-status`;
    
    const url = capService.getWidgetUrl(sessionId, checkoutId, redirectUrl);
    console.log('Widget URL:', url);
    return url;
  }, [sessionId, checkoutId, capService]);

  // Create session on mount with only merchantId
  useEffect(() => {
    if (urlParams.merchantId) {
      createInitialSession();
    } else {
      setIsCreatingSession(false);
      setError('merchantId parameter is required in URL');
    }
  }, [urlParams.merchantId, createInitialSession]);

  return {
    sessionId,
    checkoutId,
    urlParams,
    isLoading,
    isCreatingSession,
    error,
    env: urlParams.env || '',
    updateSessionAndCheckout,
    getWidgetUrl,
  };
};

